import { useLazyQuery, useMutation, useQuery } from "@apollo/client/react";
import { Grid } from "@mui/material";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from 'react-hot-toast';
import { useNavigate } from "react-router-dom";
import { obtenerDataImagen } from "../../../../../../actions/ImagenAction";
import { RenderInputByTypeMobile } from "../../../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import { useVisita } from "../../../../../../context/VisitaContext";
import SAVE_VISITA, { adaptAddVisitaInput } from "../../../../../../data/graphQL/mutations/Visitas/saveVisita";
import { GET_COMBO_VISITA_VALOR, adaptCombosVisitas } from "../../../../../../data/graphQL/queries/MisVisitas/getCombosVisitaValor";
import { ID_TIPO_VISITA_TELEENFERMERIA } from "../../../MisVisitasCreationContent";
import { VALIDAR_URGENCIA_VISITA } from "../../../../../../data/graphQL/queries/MisVisitas/validaciones/validarUrgenciaVisita";
import { obtenerCoordenadas } from "../../../../constants/getUbicacion";
import { tipoArchivoVisita } from "../../../../constants/TipoArchivosVisita";
import { idTiposProfesionales } from "../../../../constants/tiposProfesionales";
import { useDeleteArchivoVisita } from "../../../../hooks/useDeleteArchivoVisita";
import { useStepSave } from "../../../../hooks/useStepSave";
import { handleChangeSolvedFileVisita } from "../../../../utils/handleChangeImageVisita";
import { validateStepFields } from "../../../../utils/visitaStepValidation";
import CardBox from "../../CardBox/CardBox";
import { MensajeAdvertenciaDialog } from "../../Dialogs/MensajeAdvertenciaDialog";
import { useValidacionDistanciaVisita } from "../../../../hooks/useValidacionDistanciaVisita";

const ID_COMBO_TIPO_VISITA = 1;
const ID_COMBO_FORMA_VISITA = 2;
const ID_COMBO_PACIENTE_DOMICILIO = 3;

const ID_TIPO_PROFESIONAL_MEDICO = 1;

const ID_FORMA_VISITA_URGENCIA = 4;

export default function DatosDeVisitas({
  readOnly = false,
  onSaveRef,
  onExposeFormatted,
  idTipoProfesional,
  idFiguraProfesional,
  isClone,
  desdeCoordinacion,
  periodoCoordinador,
  figuraProfesional,
  esVisitaCero
}) {
  const { deleteArchivo } = useDeleteArchivoVisita();
  const { validarDistancia, modal: modalDistancia } = useValidacionDistanciaVisita();

  const esMedico = idTipoProfesional === ID_TIPO_PROFESIONAL_MEDICO;
  const esEspecialista = ["ESPECIALISTA"].includes(figuraProfesional?.trim()?.toUpperCase());
  const esConsultor = ["CONSULTOR"].includes(figuraProfesional?.trim()?.toUpperCase());
  const esEmergencia = ["URGENCIA"].includes(figuraProfesional?.trim()?.toUpperCase());
  const { visita, setVisita, saveArchivoVisita } = useVisita();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({});
  const [tiposVisitas, setTiposVisitas] = useState([]);
  const [pacienteDomicilioOptions, setPacienteDomicilioOptions] = useState([]);
  const [formasVisitas, setFormasVisitas] = useState([]);
  const [finalizarLoading, setFinalizarLoading] = useState(false);
  const [modalMensajeLimiteUrgencias, setModalMensajeLimiteUrgencias] = useState(false);
  const [errorUrgencia, setErrorUrgencia] = useState(null);
  const pendingUrgenciaRef = useRef({});

  const handleChange = useCallback((name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const [save] = useMutation(SAVE_VISITA);

  const { data: dataComboVisitaValor } = useQuery(GET_COMBO_VISITA_VALOR, {
    variables: { idComboVisita: ID_COMBO_TIPO_VISITA }
  });

  const isOtroProfesional = useMemo(() => {
    return idTipoProfesional == idTiposProfesionales.KINESIOLOGO ||
      idTipoProfesional == idTiposProfesionales.PSICOLOGO ||
      idTipoProfesional == idTiposProfesionales.PSIQUIATRA ||
      idTipoProfesional == idTiposProfesionales.TERAPISTA_OCUPACIONAL ||
      idTipoProfesional == idTiposProfesionales.ASISTENTE_SOCIAL ||
      idTipoProfesional == idTiposProfesionales.REHABILITADOR;
  }, [idTipoProfesional]);


  useEffect(() => {
    if (!dataComboVisitaValor) return;
    setTiposVisitas(adaptCombosVisitas(dataComboVisitaValor));
  }, [dataComboVisitaValor]);

  const { data: dataComboVisitaValorDom } = useQuery(GET_COMBO_VISITA_VALOR, {
    variables: { idComboVisita: ID_COMBO_PACIENTE_DOMICILIO }
  });

  useEffect(() => {
    if (!dataComboVisitaValorDom) return;
    setPacienteDomicilioOptions(adaptCombosVisitas(dataComboVisitaValorDom));
  }, [dataComboVisitaValorDom]);

  const { data: dataComboVisitaValorForma } = useQuery(GET_COMBO_VISITA_VALOR, {
    variables: { idComboVisita: ID_COMBO_FORMA_VISITA }
  });

  const [validarUrgenciaVisita] = useLazyQuery(VALIDAR_URGENCIA_VISITA, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataComboVisitaValorForma) return;
    setFormasVisitas(adaptCombosVisitas(dataComboVisitaValorForma));
  }, [dataComboVisitaValorForma]);

  const performSaveVisita = async (visitaConCoordenadas, finalizar) => {
    const { data } = await save({
      variables: { visitaInput: adaptAddVisitaInput(visitaConCoordenadas, finalizar) }
    });

    const { error, valorEsperado } = data?.saveVisita || {};
    const { idVisita: idVisitaNueva, idEstado } = valorEsperado ?? {};

    if (!!error) {
      toast.error(error);
      const errorSave = new Error(error);
      errorSave.isCustomError = true;
      throw errorSave;
    }

    if (!idVisitaNueva) throw new Error("No se pudo obtener el ID de la visita");

    setVisita((prev) => ({
      ...prev,
      ...formData,
      idVisita: idVisitaNueva,
      idEstado: idEstado
    }));

    if (formData.fotoPuerta) {
      handleChangeSolvedFileVisita({
        saveArchivoVisita,
        name: 'fotoPuerta',
        fileValue: formData.fotoPuerta,
        idVisita: idVisitaNueva,
        handleChange,
        idTipoArchivo: tipoArchivoVisita.FOTO_VIVIENDA
      });
    }
  };

  const saveVisita = async (visitaData, finalizar) => {
    if (!desdeCoordinacion && moment(formData.fechaVisita).isBefore(moment().subtract(2, 'days'))) {
      toast.error("La fecha de visita no puede ser anterior a 2 días desde la fecha actual");
      const error = new Error("Validación fallida");
      error.isCustomError = true;
      throw error;
    }

    const coordenadas = await obtenerCoordenadas();
    const visitaConCoordenadas = {
      ...visitaData,
      posX: coordenadas?.longitud?.toString(),
      posY: coordenadas?.latitud?.toString()
    };

    if (visitaData.idComboVisitaValorFormaVisita === ID_FORMA_VISITA_URGENCIA) {
      const { data: dataValidar } = await validarUrgenciaVisita({
        variables: {
          idEvaluacion: visitaData.idEvaluacion,
          idProfesional: visitaData.idProfesional,
          fechaVisita: moment(visitaData?.fechaVisita).format("YYYY-MM-DD HH:mm:ss"),
          idVisita: visitaData.idVisita,
          idFiguraProfesional: visitaData.idFiguraProfesional,
          idTipoProfesional: visitaData.idTipoProfesional,
          idFrecuencia: visitaData.idFrecuencia
        }
      });

      if (!!dataValidar?.validarVisitaUrgencia?.error) {
        setErrorUrgencia(dataValidar.validarVisitaUrgencia.error);
        setModalMensajeLimiteUrgencias(true);
        return new Promise((resolve, reject) => {
          pendingUrgenciaRef.current = {
            resolve,
            reject,
            visitaConCoordenadas,
            finalizar
          };
        });
      }
    }

    return performSaveVisita(visitaConCoordenadas, finalizar);
  };

  const handleAcceptLimiteUrgencias = async () => {
    setModalMensajeLimiteUrgencias(false);
    const pending = pendingUrgenciaRef.current;

    if (!pending?.resolve) return;

    pendingUrgenciaRef.current = {};

    try {
      await performSaveVisita(pending.visitaConCoordenadas, pending.finalizar);
      pending.resolve();
    } catch (error) {
      pending.reject(error);
    }
  };

  const handleCancelLimiteUrgencias = () => {
    setModalMensajeLimiteUrgencias(false);
    const pending = pendingUrgenciaRef.current;

    if (pending?.reject) {
      const error = new Error("Validación de urgencia cancelada");
      error.isCustomError = true;
      pending.reject(error);
    }

    pendingUrgenciaRef.current = {};
  };

  useStepSave(onSaveRef, async (validate) => {
    if (!isClone) {
      if (formData.idComboVisitaValorTipoVisita == tiposVisitas.find((x) => x.displayName.toLowerCase().includes("presencial"))?.id &&
        (formData.idComboVisitaValorPacienteDomicilio === pacienteEnDomicilioMap.CANCELADO || formData.idComboVisitaValorPacienteDomicilio === pacienteEnDomicilioMap.NO)) {
        toast.error("No se puede avanzar con el formulario si el paciente no está en el domicilio.\n Debe completar los datos y presionar finalizar.");
        const error = new Error("Validación fallida");
        error.isCustomError = true;
        throw error;
      }
      if (validate) validateStepFields({ formattedInputs, formData });

      const updatedVisita = { ...visita, ...formData, periodoCoordinador, desdeCoordinacion };
      await saveVisita(updatedVisita);
    }
  });

  const handleFinalizar = async () => {
    try {
      setFinalizarLoading(true);

      validateStepFields({ formattedInputs, formData });

      const updatedVisita = { ...visita, ...formData };
      
      const resultadoValidacion = await validarDistancia(updatedVisita.idVisita, { omitirValidacion: updatedVisita?.idComboVisitaValorTipoVisita === ID_TIPO_VISITA_TELEENFERMERIA });
      if (!resultadoValidacion.permitir) return;

      await saveVisita(updatedVisita, true);

      toast.success("Visita guardada correctamente.");
      navigate("/misVisitas");
    } catch (error) {
      console.error(error);
    } finally {
      setFinalizarLoading(false);
    }
  };

  const pacienteEnDomicilioMap = useMemo(() => {
    const map = {};
    pacienteDomicilioOptions.forEach((opt) => {
      if (opt.displayName.toLowerCase().includes("no")) map.NO = opt.id;
      if (opt.displayName.toLowerCase().includes("cancelado")) map.CANCELADO = opt.id;
      if (opt.displayName.toLowerCase().includes("si")) map.SI = opt.id;
    });
    return map;
  }, [pacienteDomicilioOptions]);

  const formattedInputs = useMemo(() => {
    return inputs.map((input) => {
      const newInput = { ...input };
      const pacienteDomicilio = formData.idComboVisitaValorPacienteDomicilio;

      switch (input.name) {
        case "idComboVisitaValorTipoVisita":
          newInput.options = tiposVisitas;
          break;
        case "idComboVisitaValorFormaVisita":
          newInput.options = formasVisitas.filter((op) => esEmergencia && !op.displayName.toLowerCase().includes('programada') || !esEmergencia).
            map((opt) => ({ value: opt.id, label: opt.displayName }));
          newInput.hidden = idTipoProfesional !== idTiposProfesionales.NUTRICIONISTA && idTipoProfesional !== idTiposProfesionales.MEDICO && idTipoProfesional !== idTiposProfesionales.ENFERMERO;
          break;
        case "idComboVisitaValorPacienteDomicilio":
          newInput.options = pacienteDomicilioOptions.filter((op) => esMedico && !op.displayName.toLowerCase().includes("cancelado") || !esMedico).
            map((opt) => ({ value: opt.id, label: opt.displayName }));

          newInput.hidden = formData.idComboVisitaValorTipoVisita === tiposVisitas.find((x) => x.displayName.toLowerCase().includes("virtual"))?.id || esEspecialista;
          newInput.disabled = !!formData?.idVisita && !!visita.idComboVisitaValorPacienteDomicilio;
          break;
        case "fechaVisita":
          const hoy = new Date();
          newInput.disableFuture = true;
          newInput.minDate = !!desdeCoordinacion ? null : new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - 2);

          if (pacienteDomicilio === pacienteEnDomicilioMap.CANCELADO) {
            newInput.maxDate = null;
            newInput.disableFuture = false;
          } else {
            newInput.maxDate = hoy;
          }
          newInput.defaultValue = hoy.toISOString().split("T")[0];
          newInput.disabled = !!formData?.idVisita || isOtroProfesional || !!isClone ? true : newInput.disabled;
          break;
        case "tiempoEspera":
          newInput.hidden = !(pacienteDomicilio === pacienteEnDomicilioMap.NO) || esEspecialista;
          newInput.required = pacienteDomicilio === pacienteEnDomicilioMap.NO && !esEspecialista;
          break;
        case "explicacion":
          newInput.hidden = !(
            pacienteDomicilio === pacienteEnDomicilioMap.NO || pacienteDomicilio === pacienteEnDomicilioMap.CANCELADO) ||
            esEspecialista;
          break;
        case "fotoPuerta":
          newInput.hidden = !(pacienteDomicilio === pacienteEnDomicilioMap.NO) || esEspecialista;
          newInput.required = pacienteDomicilio === pacienteEnDomicilioMap.NO && !esEspecialista;
          newInput.allowRemove = true;
          break;
        case "guardar":
          newInput.hidden = !(pacienteDomicilio === pacienteEnDomicilioMap.NO || pacienteDomicilio === pacienteEnDomicilioMap.CANCELADO) || esEspecialista;
          break;
        default:
          break;
      }

      if (esConsultor && newInput.name != "fechaVisita") newInput.hidden = true;

      if (readOnly || esVisitaCero) {
        newInput.disabled = true;
      }

      return newInput;
    });
  }, [
    formData?.idVisita,
    formData.idComboVisitaValorPacienteDomicilio,
    visita.idComboVisitaValorPacienteDomicilio,
    formData.fechaVisita,
    readOnly,
    isClone,
    esConsultor,
    desdeCoordinacion,
    esEspecialista,
    pacienteEnDomicilioMap,
    tiposVisitas,
    isOtroProfesional,
    formData.idComboVisitaValorTipoVisita,
    pacienteDomicilioOptions,
    formasVisitas,
    esVisitaCero,
    esEmergencia]
  );

  const handleChangeFile = (name, fileValue) => {
    obtenerDataImagen(fileValue).then(async (fileData) => {
      if (!fileData) throw new Error('no hay datos correctos del archivo');

      const parsedFile = {
        contentType: fileData.tipo,
        archivo: fileData.data,
        extension: fileData.extension,
        titulo: fileData.nombre,
        idArchivo: 0,
        idTipoArchivoDocumento: tipoArchivoVisita.FOTO_VIVIENDA,
        idVisita: 0,
        fechaAlta: moment().format('YYYY/MM/DD')
      }
      handleChange('fotoPuerta', parsedFile);
    })
  }

  const onDeleteImage = (name, hideToast) => {
    const file = formData[name];
    deleteArchivo({
      idArchivo: file.idArchivo,
      name: name,
      handleChange,
      hideToast
    });
  };

  useEffect(() => {
    handleChange('idFiguraProfesional', idFiguraProfesional);
  }, [idFiguraProfesional]);

  useEffect(() => {
    //Lógica para borrar datos de una visita presencial cuando se selecciona "visita virtual"
    if (!tiposVisitas?.length) return;
    const esVirtual = formData.idComboVisitaValorTipoVisita === tiposVisitas.find((x) => x.displayName.toLowerCase().includes("virtual"))?.id;
    if (!esVirtual) return;

    handleChange('idComboVisitaValorPacienteDomicilio', null);
    handleChange('tiempoEspera', null);
    handleChange('explicacion', null);
    if (!!formData.fotoPuerta)
      onDeleteImage('fotoPuerta', true);
  }, [formData.idComboVisitaValorTipoVisita, tiposVisitas]);

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      ...visita,
      idComboVisitaValorTipoVisita:
        visita?.idComboVisitaValorTipoVisita || prev.idComboVisitaValorTipoVisita,
    }));
  }, [visita]);


  useEffect(() => {
    onExposeFormatted?.({
      stepId: 'datosDeVisita',
      stepName: 'Datos de visita',
      formattedInputs
    });
  }, [onExposeFormatted, formattedInputs]);

  return (
    <CardBox>
      <Grid container spacing={2}>
        {formattedInputs.
          filter((input) => !input.hidden).
          map((input) =>
            <Grid size={{ xs: 12 }} key={input.name}>
              {input.name !== 'guardar' && <RenderInputByTypeMobile
                key={input.name}
                input={input}
                value={formData[input.name] || ""}
                handleChange={input.name === 'fotoPuerta' ? handleChangeFile : handleChange}
                handleDelete={input.name === 'fotoPuerta' ? onDeleteImage : null}
                row={input.row} />
              }
              {input.name === 'guardar' && <RenderInputByTypeMobile
                key={input.name}
                input={{
                  ...input,
                  onClick: handleFinalizar,
                  isLoading: finalizarLoading
                }} />
              }
            </Grid>
          )}
      </Grid>
      <MensajeAdvertenciaDialog
        open={modalMensajeLimiteUrgencias}
        onClose={handleCancelLimiteUrgencias}
        onAccept={handleAcceptLimiteUrgencias}
        onCancel={handleCancelLimiteUrgencias}
        message={errorUrgencia}
        acceptText='Continuar'
        cancelText='Cancelar'
      />
      {modalDistancia}
    </CardBox>);

}

const inputs = [
  {
    displayName: "Tipo de visita",
    name: "idComboVisitaValorTipoVisita",
    type: "select",
    required: true
  },
  {
    displayName: "Programada",
    name: "idComboVisitaValorFormaVisita",
    type: "multiple_boolean",
    required: true,
    row: true,
    lightTitle: true
  },
  {
    displayName: "Paciente en domicilio",
    name: "idComboVisitaValorPacienteDomicilio",
    type: "multiple_boolean",
    required: true,
    row: true,
    lightTitle: true
  },
  {
    displayName: "Fecha y Hora de ingreso",
    name: "fechaVisita",
    type: "datetime",
    required: true,
    disableFuture: true
  },
  {
    displayName: "Tiempo de espera (minutos)",
    name: "tiempoEspera",
    type: "int",
    min: 0
  },
  {
    displayName: "Explicación",
    name: "explicacion",
    type: "long_string",
    required: true,
    maxLength: 4000
  },
  {
    displayName: "Foto puerta",
    name: "fotoPuerta",
    type: "file",
    validExtensions: ['JPG', 'JPEG', 'PNG', 'PDF'],
    maxFileSize: 2
  },
  {
    displayName: "Finalizar",
    name: "guardar",
    type: "button"
  }
];